import React from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  Target,
  RotateCcw,
  Bookmark,
  ArrowRight,
  CheckCircle2,
  LifeBuoy,
  PlayCircle,
  ShieldCheck,
  Timer,
  Trophy,
  CalendarCheck,
  BookOpenCheck,
  FileQuestion,
  Layers,
  GraduationCap,
  CalendarClock,
  MessageSquareHeart,
} from "lucide-react";
import StudyShell from "@/components/study/StudyShell";
import { getLearningProgress, getStudyDashboard, getStudyPlan } from "@/lib/api";
import { PlanTasks } from "@/pages/study/StudyPlan";
import { STUDY } from "@/constants/testIds";
import { useStudyAuth } from "@/context/StudyAuth";
import { LEARN_CURRICULUM } from "@/data/learnCurriculum";
import LiveReviewPromo from "@/components/LiveReviewPromo";

const SUPPORT_EMAIL = "support@illinoisbrokerprep.com";

const Stat = ({ label, value }) => (
  <div className="rounded-2xl border border-[color:rgba(10,27,51,0.12)] bg-white p-5 text-center shadow-[0_10px_30px_rgba(7,20,39,0.05)]">
    <p className="text-xs font-semibold uppercase tracking-wide text-[color:rgba(11,18,32,0.55)]">
      {label}
    </p>
    <p className="mt-1 text-2xl font-bold text-[var(--ibp-ink)]">{value}</p>
  </div>
);

function sectionScore(sections, key) {
  const s = (sections || {})[key] || { correct: 0, attempted: 0 };
  return `${s.correct}/${s.attempted}`;
}

const MiniStat = ({ label, value }) => (
  <div className="rounded-xl bg-[var(--ibp-offwhite)] px-3 py-2 text-center">
    <p className="text-[10px] font-semibold uppercase tracking-wide text-[color:rgba(11,18,32,0.5)]">
      {label}
    </p>
    <p className="mt-0.5 text-sm font-bold text-[var(--ibp-ink)]">{value}</p>
  </div>
);

function ReadinessRing({ readiness }) {
  const r = readiness || {};
  const score = Math.max(0, Math.min(100, Number(r.score) || 0));
  const size = 132;
  const stroke = 12;
  const radius = (size - stroke) / 2;
  const circ = 2 * Math.PI * radius;
  const dash = (score / 100) * circ;
  const color =
    score >= 85
      ? "#15803d"
      : score >= 70
      ? "var(--ibp-gold-500)"
      : score >= 50
      ? "#d97706"
      : "#B42318";
  const isReady = r.label === "Ready for simulation";

  return (
    <div
      data-testid={STUDY.readinessRing}
      className="mt-6 flex flex-col items-center gap-5 rounded-2xl border border-[color:rgba(10,27,51,0.12)] bg-white p-6 shadow-[0_10px_30px_rgba(7,20,39,0.05)] sm:flex-row sm:items-center"
    >
      <div className="relative flex-none" style={{ width: size, height: size }}>
        <svg width={size} height={size} className="-rotate-90">
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            stroke="rgba(10,27,51,0.10)"
            strokeWidth={stroke}
            fill="none"
          />
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            stroke={color}
            strokeWidth={stroke}
            strokeLinecap="round"
            fill="none"
            strokeDasharray={`${dash} ${circ}`}
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-3xl font-bold text-[var(--ibp-ink)]">{score}</span>
          <span className="text-[10px] font-semibold uppercase tracking-wide text-[color:rgba(11,18,32,0.45)]">
            / 100
          </span>
        </div>
      </div>

      <div className="flex-1 text-center sm:text-left">
        <div className="flex flex-wrap items-center justify-center gap-2 sm:justify-start">
          <h2 className="text-base font-semibold text-[var(--ibp-ink)]">Study readiness</h2>
          <span
            className={[
              "rounded-full px-2.5 py-1 text-[11px] font-semibold",
              isReady
                ? "bg-[color:rgba(21,128,61,0.12)] text-[#15803d]"
                : "bg-[var(--ibp-offwhite)] text-[color:rgba(11,18,32,0.7)]",
            ].join(" ")}
          >
            {r.label || "—"}
          </span>
        </div>
        <p className="mt-1.5 text-sm leading-6 text-[color:rgba(11,18,32,0.7)]">{r.basis}</p>
        <div className="mt-3 grid grid-cols-3 gap-2">
          <MiniStat label="Coverage" value={`${r.coverage_pct ?? 0}%`} />
          <MiniStat label="Accuracy" value={`${r.accuracy_pct ?? 0}%`} />
          <MiniStat label="Open missed" value={r.open_missed ?? 0} />
        </div>
        {!isReady && (
          <p className="mt-2 text-[11px] text-[color:rgba(11,18,32,0.5)]">
            A score of 85+ with at least 80% coverage unlocks the &ldquo;Ready for simulation&rdquo; label.
          </p>
        )}
        <p className="mt-2 text-[11px] text-[color:rgba(11,18,32,0.5)]">
          Study guidance only. This score is not a pass prediction or guarantee.
        </p>
      </div>
    </div>
  );
}

function SectionReadiness({ items }) {
  if (!items?.length) return null;
  return (
    <div data-testid={STUDY.sectionReadiness} className="mt-4 grid gap-4 sm:grid-cols-2">
      {items.map((item) => (
        <div
          key={item.section}
          className="border border-[color:rgba(10,27,51,0.12)] bg-white p-5 shadow-[0_10px_30px_rgba(7,20,39,0.04)]"
        >
          <div className="flex items-start justify-between gap-3">
            <div>
              <h2 className="text-sm font-semibold text-[var(--ibp-ink)]">{item.section}</h2>
              <p className="mt-1 text-xs text-[color:rgba(11,18,32,0.58)]">
                {item.practice_target}% simulation target
              </p>
            </div>
            <span className="text-2xl font-bold text-[var(--ibp-ink)]">{item.score}</span>
          </div>
          <div className="mt-4 h-2 overflow-hidden rounded-full bg-[color:rgba(10,27,51,0.08)]">
            <div
              className="h-full rounded-full bg-[var(--ibp-gold-500)]"
              style={{ width: `${Math.max(0, Math.min(100, item.score || 0))}%` }}
            />
          </div>
          <div className="mt-3 flex justify-between text-xs text-[color:rgba(11,18,32,0.62)]">
            <span>{item.coverage_pct}% coverage</span>
            <span>{item.accuracy_pct}% accuracy</span>
          </div>
        </div>
      ))}
    </div>
  );
}

function ExamWeekPrompt({ plan }) {
  const days = plan?.days_until_exam;
  if (typeof days !== "number" || days < 0 || days > 7) return null;
  return (
    <div className="mt-6 flex flex-col gap-3 border-l-4 border-l-[var(--ibp-gold-500)] bg-[var(--ibp-navy-950)] p-5 text-white sm:flex-row sm:items-center sm:justify-between">
      <div>
        <h2 className="flex items-center gap-2 text-base font-semibold">
          <CalendarClock className="h-4 w-4 text-[var(--ibp-gold-500)]" /> Exam Week is ready
        </h2>
        <p className="mt-1 text-sm text-white/70">
          {days === 0 ? "Your exam is today." : `${days} day${days === 1 ? "" : "s"} to go.`} Focus your final review.
        </p>
      </div>
      <Link
        to="/study/exam-week"
        className="inline-flex min-h-10 flex-none items-center justify-center gap-2 rounded-xl bg-[var(--ibp-gold-500)] px-4 py-2 text-sm font-semibold text-[var(--ibp-ink)]"
      >
        Open Exam Week <ArrowRight className="h-4 w-4" />
      </Link>
    </div>
  );
}

function FeedbackPrompt({ feedback }) {
  if (!feedback?.should_prompt) return null;
  return (
    <div className="mt-6 flex flex-col gap-3 border border-[color:rgba(10,27,51,0.12)] bg-white p-5 sm:flex-row sm:items-center sm:justify-between">
      <div>
        <h2 className="flex items-center gap-2 text-base font-semibold text-[var(--ibp-ink)]">
          <MessageSquareHeart className="h-4 w-4 text-[#B7791F]" /> Help us improve
        </h2>
        <p className="mt-1 text-sm text-[color:rgba(11,18,32,0.65)]">
          You have enough study time for useful feedback. It takes about one minute.
        </p>
      </div>
      <Link
        to="/study/feedback"
        className="inline-flex min-h-10 flex-none items-center justify-center gap-2 rounded-xl border border-[color:rgba(10,27,51,0.18)] px-4 py-2 text-sm font-semibold text-[var(--ibp-ink)]"
      >
        Share feedback <ArrowRight className="h-4 w-4" />
      </Link>
    </div>
  );
}

function TodayPlanCard({ plan }) {
  if (!plan) {
    return (
      <div
        data-testid={STUDY.planTodayCard}
        className="mt-6 flex flex-col gap-3 rounded-2xl border border-dashed border-[color:rgba(214,162,30,0.5)] bg-[color:rgba(214,162,30,0.06)] p-6 sm:flex-row sm:items-center sm:justify-between"
      >
        <div>
          <h2 className="flex items-center gap-2 text-base font-semibold text-[var(--ibp-ink)]">
            <CalendarCheck className="h-4 w-4" /> Build your study plan
          </h2>
          <p className="mt-1 text-sm text-[color:rgba(11,18,32,0.7)]">
            Get a day-by-day path to exam day, tailored to your weak topics and missed questions.
          </p>
        </div>
        <Link
          to="/study/plan"
          className="inline-flex flex-none items-center gap-1.5 rounded-xl bg-[var(--ibp-navy-950)] px-5 py-2.5 text-sm font-semibold text-white hover:opacity-90"
        >
          Create a plan <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
    );
  }

  const today = (plan.days || []).find((d) => d.day === plan.today_day);
  return (
    <div
      data-testid={STUDY.planTodayCard}
      className="mt-6 rounded-2xl border border-[var(--ibp-gold-500)] bg-white p-6 shadow-[0_10px_30px_rgba(7,20,39,0.05)] ring-1 ring-[var(--ibp-gold-500)]"
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="flex items-center gap-2 text-base font-semibold text-[var(--ibp-ink)]">
          <CalendarCheck className="h-4 w-4 text-[var(--ibp-gold-500)]" /> Today&apos;s plan
          <span className="text-xs font-medium text-[color:rgba(11,18,32,0.55)]">
            ({plan.label} · {plan.progress.completed}/{plan.progress.total} days)
          </span>
        </h2>
        <Link
          to="/study/plan"
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-[var(--ibp-navy-950)] hover:underline"
        >
          View full plan <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
      {today ? (
        <>
          <p className="mt-2 text-sm font-semibold text-[var(--ibp-ink)]">
            Day {today.day}: {today.title}
          </p>
          <div className="mt-3">
            <PlanTasks tasks={today.tasks} />
          </div>
        </>
      ) : (
        <p className="mt-2 text-sm text-[color:rgba(11,18,32,0.7)]">
          Your plan is complete — nice work. Keep reviewing missed questions and use Exam Mode to stay sharp.
        </p>
      )}
    </div>
  );
}

const ActionLink = ({ to, children, icon: Icon, variant = "primary" }) => {
  const classes =
    variant === "primary"
      ? "bg-[var(--ibp-gold-500)] text-[var(--ibp-ink)] shadow-[0_14px_30px_rgba(214,162,30,0.18)] hover:bg-[var(--ibp-primary-hover)]"
      : "border border-[color:rgba(10,27,51,0.16)] bg-white text-[var(--ibp-ink)] hover:border-[var(--ibp-navy-950)]";

  return (
    <Link
      to={to}
      className={[
        "inline-flex min-h-11 items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold transition-colors",
        classes,
      ].join(" ")}
    >
      {Icon ? <Icon className="h-4 w-4" /> : null}
      {children}
      <ArrowRight className="h-4 w-4 opacity-70" />
    </Link>
  );
};

function getResumeAction(data, learningCompleted = 0) {
  if ((data?.missed_count || 0) > 0) {
    return {
      to: "/study/missed",
      label: "Review missed questions",
      detail: "Clean up open misses before adding more volume.",
      icon: RotateCcw,
    };
  }
  if (learningCompleted < LEARN_CURRICULUM.length) {
    return {
      to: "/study/learn",
      label: learningCompleted ? "Continue learning" : "Start learning",
      detail: "Learn the rule and key terms before you drill more questions.",
      icon: GraduationCap,
    };
  }
  if ((data?.attempted_count || 0) > 0 && (data?.remaining_count || 0) > 0) {
    return {
      to: "/study/question-bank",
      label: "Continue question bank",
      detail: "Keep building coverage across the remaining questions.",
      icon: BookOpenCheck,
    };
  }
  if ((data?.sprints?.attempts_count || 0) > 0) {
    return {
      to: "/study/mini-exams",
      label: "Open exam mode",
      detail: "Use timed review to build speed and stamina.",
      icon: FileQuestion,
    };
  }
  return null;
}

function WelcomeStartPanel({ firstName, data, plan, learningCompleted = 0, hasFullAccess = true }) {
  const hasProgress =
    (data?.attempted_count || 0) > 0 ||
    (data?.sprints?.attempts_count || 0) > 0 ||
    (plan?.progress?.completed || 0) > 0 ||
    learningCompleted > 0;
  const resume = getResumeAction(data, learningCompleted);
  const heading = hasProgress
    ? firstName
      ? `Keep going, ${firstName}`
      : "Keep going"
    : firstName
    ? `Welcome, ${firstName}`
    : "Welcome";
  const path = [
    { label: "Learn the rule", detail: "Use short Illinois-first lessons and key terms." },
    { label: "Recall and drill", detail: "Use lesson checkpoints and focused topic practice." },
    { label: "Build speed", detail: "Use timed review sprints and exam simulations." },
  ];

  return (
    <section className="mt-6 overflow-hidden rounded-2xl border border-[color:rgba(10,27,51,0.12)] bg-white shadow-[0_10px_30px_rgba(7,20,39,0.06)]">
      <div className="border-b border-[color:rgba(10,27,51,0.08)] bg-[color:rgba(214,162,30,0.08)] px-5 py-5 sm:px-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-[color:rgba(11,18,32,0.55)]">
              Start here
            </p>
            <h2 className="mt-1 text-xl font-bold text-[var(--ibp-ink)]">
              {heading}
            </h2>
            <p className="mt-1.5 max-w-2xl text-sm leading-6 text-[color:rgba(11,18,32,0.7)]">
              {hasProgress
                ? resume?.detail || "Pick up your study work where you left off."
                : "Start with a short lesson, then use its checkpoint and your weak areas to guide practice."}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {hasProgress && resume ? (
              <ActionLink to={resume.to} icon={resume.icon}>
                {resume.label}
              </ActionLink>
            ) : (
              <ActionLink to="/study/learn" icon={GraduationCap}>
                Start learning
              </ActionLink>
            )}
            <ActionLink to="/study/question-bank" icon={BookOpenCheck} variant="secondary">
              Question bank
            </ActionLink>
            <ActionLink to="/study/plan" icon={CalendarCheck} variant="secondary">
              Study plan
            </ActionLink>
          </div>
        </div>
      </div>

      <div className="grid gap-0 lg:grid-cols-[1.1fr_0.9fr]">
        <div className="border-b border-[color:rgba(10,27,51,0.08)] p-5 sm:p-6 lg:border-b-0 lg:border-r">
          <h3 className="flex items-center gap-2 text-sm font-semibold text-[var(--ibp-ink)]">
            <PlayCircle className="h-4 w-4 text-[var(--ibp-gold-500)]" /> Recommended path
          </h3>
          <div className="mt-4 space-y-3">
            {path.map((step, idx) => (
              <div key={step.label} className="flex gap-3">
                <span className="flex h-7 w-7 flex-none items-center justify-center rounded-full bg-[var(--ibp-navy-950)] text-xs font-bold text-white">
                  {idx + 1}
                </span>
                <div>
                  <p className="text-sm font-semibold text-[var(--ibp-ink)]">{step.label}</p>
                  <p className="mt-0.5 text-xs leading-5 text-[color:rgba(11,18,32,0.58)]">
                    {step.detail}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="p-5 sm:p-6">
          <h3 className="flex items-center gap-2 text-sm font-semibold text-[var(--ibp-ink)]">
            <ShieldCheck className="h-4 w-4 text-[#15803d]" /> {hasFullAccess ? "Lifetime access active" : "Free account active"}
          </h3>
          <div className="mt-4 space-y-3 text-sm text-[color:rgba(11,18,32,0.7)]">
            <div className="flex items-start gap-2">
              <CheckCircle2 className="mt-0.5 h-4 w-4 flex-none text-[#15803d]" />
              <span>{hasFullAccess ? "The complete study platform is unlocked." : "Your starter lessons and practice set are unlocked."}</span>
            </div>
            <div className="flex items-start gap-2">
              <CheckCircle2 className="mt-0.5 h-4 w-4 flex-none text-[#15803d]" />
              <span>Your progress saves securely to this account.</span>
            </div>
            {!hasFullAccess ? (
              <div className="flex items-start gap-2">
                <ShieldCheck className="mt-0.5 h-4 w-4 flex-none text-[var(--ibp-gold-500)]" />
                <span><Link to="/product" className="font-semibold text-[var(--ibp-navy-950)] hover:underline">Upgrade once for $79</Link> to unlock every lesson, simulation, and study tool.</span>
              </div>
            ) : null}
            <div className="flex items-start gap-2">
              <LifeBuoy className="mt-0.5 h-4 w-4 flex-none text-[var(--ibp-gold-500)]" />
              <span>
                Need help?{" "}
                <a className="font-semibold text-[var(--ibp-navy-950)] underline-offset-4 hover:underline" href={`mailto:${SUPPORT_EMAIL}`}>
                  {SUPPORT_EMAIL}
                </a>
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function StudyDashboard() {
  const { license, hasFullAccess } = useStudyAuth();
  const firstName = (license?.first_name || "").trim();
  const { data, isLoading } = useQuery({
    queryKey: ["study-dashboard"],
    queryFn: getStudyDashboard,
  });
  const { data: plan } = useQuery({
    queryKey: ["study-plan"],
    queryFn: getStudyPlan,
    enabled: hasFullAccess,
  });
  const { data: learningProgress } = useQuery({
    queryKey: ["learning-progress"],
    queryFn: getLearningProgress,
  });

  return (
    <StudyShell testId={STUDY.dashboard}>
      <h1 className="text-2xl font-bold text-[var(--ibp-ink)]">
        {firstName ? `Welcome, ${firstName}` : "Your dashboard"}
      </h1>
      <p className="mt-1 text-sm text-[color:rgba(11,18,32,0.6)]">
        Track your progress and focus where it counts.
      </p>

      {isLoading || !data ? (
        <p className="mt-8 text-sm text-[color:rgba(11,18,32,0.6)]">Loading...</p>
      ) : (
        <>
          <WelcomeStartPanel
            firstName={firstName}
            data={data}
            plan={plan}
            learningCompleted={(learningProgress?.completed_ids || []).length}
            hasFullAccess={hasFullAccess}
          />

          {data.readiness && <ReadinessRing readiness={data.readiness} />}

          <SectionReadiness items={data.section_readiness} />

          {hasFullAccess ? <TodayPlanCard plan={plan} /> : null}

          {hasFullAccess ? <ExamWeekPrompt plan={plan} /> : null}

          <FeedbackPrompt feedback={data.feedback} />

          <LiveReviewPromo source="dashboard" compact />

          <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-4">
            <Stat label="Overall" value={`${data.overall.correct}/${data.overall.attempted || 0}`} />
            <Stat label="Illinois State" value={sectionScore(data.sections, "state")} />
            <Stat label="National" value={sectionScore(data.sections, "national")} />
            <Stat label="Math" value={sectionScore(data.sections, "math")} />
          </div>

          <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-4">
            <Stat label="Attempted" value={data.attempted_count} />
            <Stat label="Remaining" value={data.remaining_count} />
            <Stat label="Missed" value={data.missed_count} />
            <Stat label="Bookmarked" value={data.bookmarked_count} />
          </div>

          <div className="mt-6 rounded-2xl border border-[color:rgba(10,27,51,0.12)] bg-white p-6 shadow-[0_10px_30px_rgba(7,20,39,0.05)]">
            <h2 className="text-base font-semibold text-[var(--ibp-ink)]">
              Recommended next action
            </h2>
            <p className="mt-1.5 text-sm text-[color:rgba(11,18,32,0.75)]">
              {data.recommended_action}
            </p>
            {data.weakest_topics && data.weakest_topics.length > 0 && (
              <div className="mt-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-[color:rgba(11,18,32,0.5)]">
                  Weakest topics
                </p>
                <ul className="mt-2 space-y-1.5">
                  {data.weakest_topics.map((t) => (
                    <li key={t} className="flex items-center gap-2 text-sm text-[var(--ibp-ink)]">
                      <span className="h-1.5 w-1.5 rounded-full bg-[var(--ibp-gold-500)]" />
                      {t}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
            <Link to="/study/learn" className="flex items-center justify-between rounded-2xl border border-[color:rgba(10,27,51,0.12)] bg-white p-5 hover:border-[var(--ibp-navy-950)]">
              <span className="flex items-center gap-2 text-sm font-semibold text-[var(--ibp-ink)]"><GraduationCap className="h-4 w-4" /> Learn</span>
              <ArrowRight className="h-4 w-4 text-[color:rgba(11,18,32,0.4)]" />
            </Link>
            <Link to="/study/question-bank" className="flex items-center justify-between rounded-2xl border border-[color:rgba(10,27,51,0.12)] bg-white p-5 hover:border-[var(--ibp-navy-950)]">
              <span className="flex items-center gap-2 text-sm font-semibold text-[var(--ibp-ink)]"><BookOpenCheck className="h-4 w-4" /> Question Bank</span>
              <ArrowRight className="h-4 w-4 text-[color:rgba(11,18,32,0.4)]" />
            </Link>
            <Link to="/study/practice" className="flex items-center justify-between rounded-2xl border border-[color:rgba(10,27,51,0.12)] bg-white p-5 hover:border-[var(--ibp-navy-950)]">
              <span className="flex items-center gap-2 text-sm font-semibold text-[var(--ibp-ink)]"><Target className="h-4 w-4" /> Practice Sets</span>
              <ArrowRight className="h-4 w-4 text-[color:rgba(11,18,32,0.4)]" />
            </Link>
            <Link to="/study/mini-exams" className="flex items-center justify-between rounded-2xl border border-[color:rgba(10,27,51,0.12)] bg-white p-5 hover:border-[var(--ibp-navy-950)]">
              <span className="flex items-center gap-2 text-sm font-semibold text-[var(--ibp-ink)]"><FileQuestion className="h-4 w-4" /> Exam Mode</span>
              <ArrowRight className="h-4 w-4 text-[color:rgba(11,18,32,0.4)]" />
            </Link>
            <Link to="/study/flashcards" className="flex items-center justify-between rounded-2xl border border-[color:rgba(10,27,51,0.12)] bg-white p-5 hover:border-[var(--ibp-navy-950)]">
              <span className="flex items-center gap-2 text-sm font-semibold text-[var(--ibp-ink)]"><Layers className="h-4 w-4" /> Word Bank</span>
              <ArrowRight className="h-4 w-4 text-[color:rgba(11,18,32,0.4)]" />
            </Link>
          </div>

          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Link to="/study/missed" className="flex items-center justify-between rounded-2xl border border-[color:rgba(10,27,51,0.12)] bg-white p-5 hover:border-[var(--ibp-navy-950)]">
              <span className="flex items-center gap-2 text-sm font-semibold text-[var(--ibp-ink)]"><RotateCcw className="h-4 w-4" /> Missed Questions</span>
              <ArrowRight className="h-4 w-4 text-[color:rgba(11,18,32,0.4)]" />
            </Link>
            <Link to="/study/bookmarks" className="flex items-center justify-between rounded-2xl border border-[color:rgba(10,27,51,0.12)] bg-white p-5 hover:border-[var(--ibp-navy-950)]">
              <span className="flex items-center gap-2 text-sm font-semibold text-[var(--ibp-ink)]"><Bookmark className="h-4 w-4" /> Bookmarks</span>
              <ArrowRight className="h-4 w-4 text-[color:rgba(11,18,32,0.4)]" />
            </Link>
          </div>

          {/* Exam Mode summary */}
          <div className="mt-6 rounded-2xl border border-[color:rgba(10,27,51,0.12)] bg-white p-6 shadow-[0_10px_30px_rgba(7,20,39,0.05)]">
            <div className="flex items-center justify-between">
              <h2 className="flex items-center gap-2 text-base font-semibold text-[var(--ibp-ink)]">
                <Timer className="h-4 w-4" /> Exam Mode
              </h2>
              <Link
                to="/study/mini-exams"
                className="inline-flex items-center gap-1.5 text-sm font-semibold text-[var(--ibp-navy-950)] hover:underline"
              >
                Open exam mode <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
            {data.sprints && data.sprints.attempts_count > 0 ? (
              <>
                <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-5">
                  <Stat
                    label="Latest"
                    value={data.sprints.latest ? `${data.sprints.latest.percent_score}%` : "—"}
                  />
                  <Stat
                    label="Best — IL State"
                    value={
                      data.sprints.best_by_type["illinois-state-focus"]
                        ? `${data.sprints.best_by_type["illinois-state-focus"].percent_score}%`
                        : "—"
                    }
                  />
                  <Stat
                    label="Best — National"
                    value={
                      data.sprints.best_by_type["national-concepts"]
                        ? `${data.sprints.best_by_type["national-concepts"].percent_score}%`
                        : "—"
                    }
                  />
                  <Stat
                    label="Best — Mixed"
                    value={
                      data.sprints.best_by_type["mixed-final-sprint"]
                        ? `${data.sprints.best_by_type["mixed-final-sprint"].percent_score}%`
                        : "—"
                    }
                  />
                  <Stat
                    label="Best — Full"
                    value={
                      data.sprints.best_by_type["full-mixed-mock-exam"]
                        ? `${data.sprints.best_by_type["full-mixed-mock-exam"].percent_score}%`
                        : "—"
                    }
                  />
                </div>
                {data.sprints.recent && data.sprints.recent.length > 0 && (
                  <ul className="mt-4 space-y-2">
                    {data.sprints.recent.slice(0, 3).map((a) => (
                      <li
                        key={a.attempt_id}
                        className="flex items-center justify-between gap-2 rounded-xl bg-[var(--ibp-offwhite)] px-4 py-2.5 text-sm"
                      >
                        <span className="flex items-center gap-2 font-medium text-[var(--ibp-ink)]">
                          <Trophy className="h-3.5 w-3.5 text-[var(--ibp-gold-500)]" />
                          {a.sprint_title}
                        </span>
                        <Link
                          to={`/study/mini-exams/results/${a.attempt_id}`}
                          className="font-semibold text-[var(--ibp-ink)]"
                        >
                          {a.percent_score}%
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
                <p className="mt-3 text-sm text-[color:rgba(11,18,32,0.7)]">
                  {data.sprints.recommended_action}
                </p>
              </>
            ) : (
              <p className="mt-2 text-sm text-[color:rgba(11,18,32,0.65)]">
                {data.sprints
                  ? data.sprints.recommended_action
                  : "Open Exam Mode to build speed and pinpoint weak topics."}
              </p>
            )}
          </div>
        </>
      )}
    </StudyShell>
  );
}
